import {FaIcon, PageHeader} from 'client/js/util/layout_utils';
import {Link} from 'react-router-dom';
import { useEffect, useState } from 'react';
import ConfirmedDelete from 'client/js/util/confirmed_delete';
import HandleDetails from './handle';
import {useQuery, useMutation} from '@tanstack/react-query';
import LoadingTableSpinner from 'client/js/util/loading_table_spinner';
import {errorToComponent, errorToToast, parseXHRError} from 'client/js/util/rest_utils';
import {toast} from 'react-toastify';
import { Modal, ModalHeader } from 'client/js/util/modal';

const fetchData = async (id) => {
  const response = await $.ajax({ url: `/usercp/handles/${id}`, dataType: 'json' });

  return response;
}

const getCooldownSeconds = (payload) => {
  const parsed = Number.parseInt(payload?.cooldown_seconds_remaining, 10);

  if (Number.isNaN(parsed)) {
    return 0;
  }

  return Math.max(parsed, 0);
};

const parseJsonFromXhr = (xhr) => {
  if (xhr?.responseJSON) {
    return xhr.responseJSON;
  }

  try {
    return JSON.parse(xhr?.responseText || '{}');
  } catch {
    return {};
  }
};

const IcannValidationBanner = ({ handle, refetch }) => {
  const [emailSent, setEmailSent] = useState(false);
  const [verificationModalOpen, setVerificationModalOpen] = useState(false);
  const [triggerCode, setTriggerCode] = useState('');
  const [triggerErrors, setTriggerErrors] = useState(null);
  const [requestTokenError, setRequestTokenError] = useState(null);
  const [tokenCooldownSeconds, setTokenCooldownSeconds] = useState(0);
  const [isVerified, setIsVerified] = useState(handle.icann_verified);

  useEffect(() => {
    setEmailSent(false);
    setVerificationModalOpen(false);
    setTriggerCode('');
    setTriggerErrors(null);
    setRequestTokenError(null);
    setTokenCooldownSeconds(0);
    setIsVerified(false);
  }, [handle.id]);

  useEffect(() => {
    if (handle.icann_verified) {
      setIsVerified(true);
    }
  }, [handle.icann_verified]);

  useEffect(() => {
    if (tokenCooldownSeconds <= 0) {
      return undefined;
    }

    const timerId = window.setInterval(() => {
      setTokenCooldownSeconds((prev) => (prev > 1 ? prev - 1 : 0));
    }, 1000);

    return () => {
      window.clearInterval(timerId);
    };
  }, [tokenCooldownSeconds]);

  const { isLoading: isRequestTokenLoading, mutate: requestToken } = useMutation(() => {
    return $.ajax({
      url: `/usercp/handles/${handle.id}/request_verification_token`,
      type: 'POST',
      dataType: 'json'
    });
  }, {
    onMutate: () => {
      setRequestTokenError(null);
    },
    onSuccess: (data) => {
      toast.success(data.message || 'Verification-Token wurde erfolgreich gesendet!');
      setEmailSent(true);
      setRequestTokenError(null);
      setTokenCooldownSeconds(getCooldownSeconds(data));
    },
    onError: (error) => {
      const json = parseJsonFromXhr(error);
      const cooldownSeconds = getCooldownSeconds(json);
      const serverMessage = json?.message;
      const { title, message } = parseXHRError(error);
      const fallbackMessage = serverMessage || message || title || 'Fehler beim Senden des Verification-Tokens.';

      if (cooldownSeconds > 0) {
        setEmailSent(true);
        setRequestTokenError(fallbackMessage);
        setTokenCooldownSeconds(cooldownSeconds);
        toast.error(fallbackMessage, { autoClose: false });
        return;
      }

      setRequestTokenError(fallbackMessage);
      toast.error(fallbackMessage, { autoClose: false });
    }
  });

  const { isLoading: isVerifyTokenLoading, mutate: verifyToken } = useMutation((trigger) => {
    return $.ajax({
      url: `/usercp/handles/${handle.id}/verify_verification_token`,
      type: 'POST',
      dataType: 'json',
      data: {
        trigger
      }
    });
  }, {
    onMutate: () => {
      setTriggerErrors(null);
    },
    onSuccess: (data) => {
      toast.success(data.message || 'Dein Kontakt wurde erfolgreich verifiziert.');
      setIsVerified(true);
      setVerificationModalOpen(false);
      setTriggerCode('');
      setTriggerErrors(null);
      refetch?.();
    },
    onError: (error) => {
      const { isValidationError, validationErrors, message } = parseXHRError(error);

      if (isValidationError) {
        setTriggerErrors(validationErrors?.trigger || validationErrors?.base || ['Der eingegebene Triggercode ist ungültig.']);
        return;
      }

      setTriggerErrors(null);

      if (message) {
        toast.error(message, { autoClose: false });
        return;
      }

      errorToToast(error, "beim Verifizieren des Tokens");
    }
  });

  if (!handle.needs_icann_verification) {
    return null;
  }

  const handleRequestTokenClick = (e) => {
    e.preventDefault();

    if (tokenCooldownSeconds > 0) {
      return;
    }

    requestToken();
  }

  const handleVerifyToken = (e) => {
    e.preventDefault();
    const trimmedToken = triggerCode.trim();

    if (!trimmedToken) {
      setTriggerErrors(['Bitte gib einen Validierungscode ein.']);
      return;
    }

    verifyToken(trimmedToken);
  };

  const openVerificationModal = () => {
    setTriggerErrors(null);
    setRequestTokenError(null);
    setVerificationModalOpen(true);
  };

  const closeVerificationModal = () => {
    if (isRequestTokenLoading || isVerifyTokenLoading) {
      return;
    }

    setTriggerErrors(null);
    setRequestTokenError(null);
    setVerificationModalOpen(false);
  };

  if (isVerified) {
    return (
      <div className="alert alert-success">
        <strong>ICANN-Verifizierung erfolgreich</strong>
        {' '}
        Der Domaininhaberkontakt ist verifiziert. Falls Du Domains mit diesem Kontakt besitzt, die noch auf &quot;ICANN Suspended&quot; oder &quot;ICANN Unverified&quot; stehen, kann es bis zu einer Stunde dauern, bis die Domain als validiert markiert wird.
      </div>
    );
  }

  return (
    <div className="alert alert-warning">
      <div className="d-flex justify-content-between align-items-center">
        <div>
          <strong>ICANN-Verifizierung erforderlich</strong>
          {' '}
          Dieser Domaininhaberkontakt ist nicht verifiziert. Für Domains mit diesem Kontakt kann eine Verifizierung erforderlich sein. Starte die Verifizierung, um den Token per E-Mail anzufordern oder direkt einzugeben.
        </div>
        <button
          type="button"
          onClick={openVerificationModal}
          className="btn btn-warning text-nowrap"
        >
          <FaIcon name="shield" /> Verifizierung starten
        </button>
      </div>

      {verificationModalOpen && (
        <Modal isOpen onRequestClose={closeVerificationModal} contentLabel="ICANN-Verifizierung">
          <ModalHeader title="ICANN-Verifizierung" onRequestClose={closeVerificationModal} />

          <div className="modal-body">
            <p>
              Fordere den Verifizierungs-Token für <strong>{handle.email}</strong> an oder gib Deinen vorhandenen Token direkt ein.
              Die Absender-Adresse lautet <strong>noreply@emailverification.trafficplex.de</strong>.
            </p>

            <button
              type="button"
              onClick={handleRequestTokenClick}
              className="btn btn-outline-secondary mb-3"
              disabled={isRequestTokenLoading || tokenCooldownSeconds > 0}
            >
              {isRequestTokenLoading && <div className="css-spinloader inline mr-1"></div>}
              {emailSent ? <><FaIcon name="envelope" /> Token erneut anfordern</> : <><FaIcon name="envelope" /> Token anfordern</>}
            </button>
            {requestTokenError && <div className="invalid-feedback d-block mb-3">{requestTokenError}</div>}
            {emailSent && (
              <div className="small text-muted mb-3">
                Token wurde versendet.
                {tokenCooldownSeconds > 0 ? ` Erneut anfordern in ${tokenCooldownSeconds} Sekunden.` : ' Du kannst jetzt erneut anfordern.'}
              </div>
            )}

            <form onSubmit={handleVerifyToken}>
              <div className="form-group mb-0">
                <label htmlFor="icann-verification-token">Validierungscode</label>
                <input
                  id="icann-verification-token"
                  type="text"
                  className={triggerErrors?.length ? 'form-control is-invalid' : 'form-control'}
                  placeholder="Token eingeben"
                  value={triggerCode}
                  onChange={(e) => {
                    setTriggerCode(e.target.value);
                    setTriggerErrors(null);
                  }}
                />
                {triggerErrors?.length ? <div className="invalid-feedback">{triggerErrors.join(', ')}</div> : null}
              </div>
            </form>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-light" onClick={closeVerificationModal} disabled={isRequestTokenLoading || isVerifyTokenLoading}>Abbrechen</button>
            <button type="button" className="btn btn-primary" onClick={handleVerifyToken} disabled={isVerifyTokenLoading || triggerCode.trim().length === 0}>
              {isVerifyTokenLoading && <div className="css-spinloader inline mr-1"></div>}
              <FaIcon name="check" /> Token prüfen
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

const Page = (props) => {
  const { data, isLoading, isError, error, refetch } = useQuery(['handle', props.match.params.id], () => fetchData(props.match.params.id));

  if(isLoading) {
    return <>
      <PageHeader text="Adresse anzeigen" back_url="/handles" />

      <LoadingTableSpinner />
    </>
  }

  if(isError) {
    return errorToComponent(error);
  }

  const handle = data.handle;

  const buttons = <>
    <Link to={`/handles/${handle.id}/edit`} className="btn btn-light"><FaIcon name="pencil" /> bearbeiten</Link>

    <ConfirmedDelete id={handle.id} disabled={!handle.deletable} resource="handles" button_text={<><FaIcon name="trash" /> löschen</>}
      button_class="btn btn-danger" text="Dies wird die Adresse löschen. Sollten gesetzliche Aufbewahrungspflichten bestehen wird die Adresse als gelöscht markiert und nach Ablauf der Aufbewahrungspflichten gelöscht."
      success_cb={() => { props.history.push('/handles') }} />
  </>;

  return <>
    <PageHeader text="Adresse anzeigen" back_url="/handles" buttons={buttons}>
      Adresse anzeigen
    </PageHeader>

    <IcannValidationBanner handle={handle} refetch={refetch} />

    <div className="row">
      <div className="col-md-6">
          <HandleDetails handle={handle} />
      </div>
    </div>
  </>;
}

export default Page;
